<template>
  <div
    v-bind:class="{
      'vue-code-input-container': true,
      'is-invalid': isInvalid,
      [className]: !!className
    }"
    v-bind:style="{ width: `${fields * fieldWidth}px` }"
  >
    <div class="d-flex">
      <template v-for="(v, index) in values">
        <input
          class="form-control text-center"
          :type="type === 'number' ? 'tel' : type"
          :pattern="type === 'number' ? '[0-9]' : null"
          :autoFocus="autoFocus && !loading && index === autoFocusIndex"
          :style="{
            width: `${fieldWidth}px`,
            height: `${fieldHeight}px`
          }"
          :key="`${id}-${index}`"
          :data-id="index"
          :value="v"
          :ref="iRefs[index]"
          @input="onValueChange"
          @focus="onFocus"
          @keydown="onKeyDown"
          :disabled="loading || disabled"
          :required="required"
          maxlength="1"
        />
      </template>
    </div>
  </div>
</template>

<script>
/**
 * Original code is from https://github.com/suweya/vue-verification-code-input.
 * I copied instead of installing to have more control in customizing this component.
 */

const KEY_CODE = {
  backspace: 8,
  left: 37,
  up: 38,
  right: 39,
  down: 40
}

export default {
  name: 'CodeInput',

  props: {
    type: {
      type: String,
      default: 'number'
    },

    className: String,

    fields: {
      type: Number,
      default: 5
    },

    fieldWidth: {
      type: Number,
      default: 58
    },

    fieldHeight: {
      type: Number,
      default: 54
    },

    autoFocus: {
      type: Boolean,
      default: true
    },

    isInvalid: {
      type: [Boolean, String, Array],
      default: false
    },

    disabled: {
      type: Boolean,
      default: false
    },

    required: {
      type: Boolean,
      default: false
    },

    change: Function,

    complete: Function,

    loading: {
      type: Boolean,
      default: false
    }
  },

  data() {
    const { fields, values } = this
    let vals
    let autoFocusIndex = 0

    if (values && values.length) {
      vals = []
      for (let i = 0; i < fields; i++) {
        vals.push(values[i] || '')
      }

      autoFocusIndex = values.length >= fields ? 0 : values.length
    } else {
      vals = Array(fields).fill('')
    }

    this.iRefs = []
    for (let i = 0; i < fields; i++) {
      this.iRefs.push(`input_${i}`)
    }

    this.id = +new Date()
    return { values: vals, autoFocusIndex }
  },

  methods: {
    focus() {
      this.$nextTick(() => {
        let input = this.$refs['input_0']
        if (input) {
          input[0].focus()
        }
      })
    },

    onFocus(e) {
      e.target.select(e)
    },

    onValueChange(e) {
      const index = parseInt(e.target.dataset.id)
      const { type, fields } = this

      if (type === 'number') {
        e.target.value = e.target.value.replace(/[^\d]/gi, '')
      }
      // this.handleKeys[index] = false;
      if (
        e.target.value === '' ||
        (type === 'number' && !e.target.validity.valid)
      ) {
        return
      }

      let next
      const value = e.target.value
      let { values } = this
      values = Object.assign([], values)

      if (value.length > 1) {
        let nextIndex = value.length + index - 1
        if (nextIndex >= fields) {
          nextIndex = fields - 1
        }

        next = this.iRefs[nextIndex]
        const split = value.split('')
        split.forEach((item, i) => {
          const cursor = index + i
          if (cursor < fields) {
            values[cursor] = item
          }
        })

        this.values = values
      } else {
        next = this.iRefs[index + 1]
        values[index] = value
        this.values = values
      }

      if (next) {
        const element = this.$refs[next][0]
        element.focus()
        element.select()
      }

      this.triggerChange(values, index + 1 === fields)
    },

    onKeyDown(e) {
      const index = parseInt(e.target.dataset.id)
      const prevIndex = index - 1
      const nextIndex = index + 1
      const prev = this.iRefs[prevIndex]
      const next = this.iRefs[nextIndex]

      switch (e.keyCode) {
        case KEY_CODE.backspace: {
          e.preventDefault()
          const vals = [...this.values]

          if (this.values[index]) {
            vals[index] = ''
            this.values = vals
            this.triggerChange(vals)
          } else if (prev) {
            vals[prevIndex] = ''
            this.$refs[prev][0].focus()
            this.values = vals
            this.triggerChange(vals)
          }
          break
        }

        case KEY_CODE.left:
          e.preventDefault()
          if (prev) {
            this.$refs[prev][0].focus()
          }
          break

        case KEY_CODE.right:
          e.preventDefault()
          if (next) {
            this.$refs[next][0].focus()
          }
          break

        case KEY_CODE.up:
        case KEY_CODE.down:
          e.preventDefault()
          break

        default:
          // this.handleKeys[index] = true;
          break
      }
    },

    triggerChange(values = this.values, onEnd = false) {
      const val = values.join('')
      this.$emit('input', val)
      this.$emit('change', val)

      if (onEnd) {
        this.$emit('complete', val)
      }
    }
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="scss" scoped>
.vue-code-input-container {
  position: relative;

  .form-control {
    font-size: 1.4rem;
    font-weight: bold;
    border-width: 2px;
    margin-right: 11.5px;
  }
}

.vue-code-input-container.is-invalid {
  .form-control:active,
  .form-control:focus {
    border-color: #dc3545;
    color: #dc3545;
  }
}
</style>
